import { UserRole } from '@/types/user.types';

/**
 * Role hierarchy - higher index = more permissions
 */
const ROLE_HIERARCHY: UserRole[] = [
  UserRole.CLIENT,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
];

/**
 * Permission definitions
 */
export const PERMISSIONS = {
  // Product permissions
  VIEW_PRODUCTS: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.CLIENT],
  CREATE_PRODUCTS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  EDIT_PRODUCTS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  DELETE_PRODUCTS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  UPLOAD_PRODUCTS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  ASSIGN_PRODUCTS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],

  // Order permissions
  VIEW_ORDERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.CLIENT],
  CREATE_ORDERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.CLIENT],
  EDIT_ORDERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  DELETE_ORDERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  UPDATE_ORDER_STATUS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],

  // User management permissions
  VIEW_USERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  CREATE_USERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  EDIT_USERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  DELETE_USERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  CHANGE_PASSWORDS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],

  // Settings permissions
  VIEW_SETTINGS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  EDIT_SETTINGS: [UserRole.SUPER_ADMIN],

  // Transaction log permissions
  VIEW_TRANSACTIONS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],

  // QR Code permissions
  VIEW_QR_CODES: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  CREATE_QR_CODES: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  DELETE_QR_CODES: [UserRole.SUPER_ADMIN, UserRole.ADMIN],

  // Copier permissions
  VIEW_COPIERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  CREATE_COPIERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  EDIT_COPIERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
  DELETE_COPIERS: [UserRole.SUPER_ADMIN, UserRole.ADMIN],

  // Backup/Restore permissions
  BACKUP_DATA: [UserRole.SUPER_ADMIN],
  RESTORE_DATA: [UserRole.SUPER_ADMIN],
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;

/**
 * Check if a role has a specific permission
 */
export function hasPermission(userRole: UserRole | null, permission: PermissionKey): boolean {
  if (!userRole) return false;
  return PERMISSIONS[permission]?.includes(userRole) ?? false;
}

/**
 * Check if user is an admin (admin or super_admin)
 */
export function isAdmin(userRole: UserRole | null): boolean {
  return userRole === UserRole.ADMIN || userRole === UserRole.SUPER_ADMIN;
}

/**
 * Check if user is a super admin
 */
export function isSuperAdmin(userRole: UserRole | null): boolean {
  return userRole === UserRole.SUPER_ADMIN;
}

/**
 * Check if user is a client
 */
export function isClient(userRole: UserRole | null): boolean {
  return userRole === UserRole.CLIENT;
}

/**
 * Check if a role meets the minimum required role
 */
export function meetsRoleRequirement(userRole: UserRole | null, requiredRole: UserRole): boolean {
  if (!userRole) return false;

  const userRoleIndex = ROLE_HIERARCHY.indexOf(userRole);
  const requiredRoleIndex = ROLE_HIERARCHY.indexOf(requiredRole);

  return userRoleIndex >= requiredRoleIndex;
}

/**
 * Get display name for a role
 */
export function getRoleDisplayName(role: UserRole): string {
  switch (role) {
    case UserRole.SUPER_ADMIN:
      return 'Super Admin';
    case UserRole.ADMIN:
      return 'Admin';
    case UserRole.CLIENT:
      return 'Client';
    default:
      return 'Unknown';
  }
}

/**
 * Get all roles for selection dropdowns
 */
export function getSelectableRoles(currentUserRole: UserRole): UserRole[] {
  // Super admins can create any role
  if (currentUserRole === UserRole.SUPER_ADMIN) {
    return [UserRole.CLIENT, UserRole.ADMIN, UserRole.SUPER_ADMIN];
  }

  // Regular admins can only create clients
  if (currentUserRole === UserRole.ADMIN) {
    return [UserRole.CLIENT];
  }

  return [];
}
